import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

// Scentreat (14/09/2026): see component-cart-items.js - a response that lands after a newer
// cart change must not repaint the drawer with lines the shopper has since removed.
let cartMutationSeq = 0;
document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  cartMutationSeq++;
});

// 21/09/2026: the checkbox is optimistic - totals change on click and the cart write follows
// (~0.8s: two app discount Functions run on every cart change). A Check out click that lands
// meanwhile is held until the write settles (8s at most), then replayed, so checkout never
// opens without the shopper's choice applied.
/** @type {Promise<void> | null} */
let protectionWrite = null;
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
    if (!button || !protectionWrite) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    Promise.race([protectionWrite, new Promise((resolve) => setTimeout(resolve, 8000))]).then(() => {
      protectionWrite = null;
      button.removeAttribute('aria-busy');
      button.click();
    });
  },
  true
);

/**
 * Optional shipping protection toggle rendered inside the cart summary.
 *
 * Backed by the hidden `shipping-protection` product, one variant per price tier.
 * Liquid picks the tier from the cart subtotal (excluding protection itself) and
 * exposes it as `data-desired-variant`; this component keeps the cart in sync:
 *
 * - toggling on adds the desired variant
 * - toggling off removes the current line
 * - when the subtotal crosses a tier boundary the section re-renders with a new
 *   `data-desired-variant`, and `#reconcile` swaps the line to the correct price
 *
 * Every mutation goes through `/cart/update.js` keyed by variant id, so a swap is
 * a single request, and asks for the owning section back to morph totals, price
 * label and switch state in one round trip.
 *
 * The dispatched event uses `action: 'update'` deliberately — `'add'` would make
 * `<cart-drawer-component auto-open>` pop the drawer open when the shopper ticks
 * the box on the cart page.
 *
 * @typedef {Object} ShippingProtectionRefs
 * @property {HTMLInputElement} [toggle] - The checkbox input. Absent in orphan mode.
 *
 * @extends {Component<ShippingProtectionRefs>}
 */
class ShippingProtection extends Component {
  // `toggle` is absent in orphan mode (protection left behind after the last product
  // was removed), so it is looked up defensively rather than declared required.

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Guards against re-entrant writes while one is already in flight. */
  #busy = false;

  // The section is morphed in place, so this element survives cart updates and
  // connectedCallback fires only once. The data attributes are what change, so
  // reconciliation is driven off them.
  static observedAttributes = ['data-desired-variant', 'data-current-variant', 'data-line-key'];

  /** @type {boolean} */
  #reconcileQueued = false;

  /** True until the first reconcile in a browser that has not seen the opt-in version yet. */
  #resetPending = false;

  /** State the shopper last picked with the checkbox; null when the cart already matches it. */
  #want = null;

  /** Protection variant in the cart as last seen, from a server render or a write response. */
  #serverVariant = '';

  /** Whether the totals on screen currently include protection. */
  #shown = false;

  /** Resolves the module-level `protectionWrite` this component opened. */
  #settle = null;

  connectedCallback() {
    super.connectedCallback();
    this.#resetPending = ShippingProtection.#firstOptInVisit();
    this.#syncFromRender();
    this.#scheduleReconcile();
  }

  /**
   * 21/09/2026: protection used to be switched on for every cart. It is opt-in now, so a line left
   * from that default is removed once per browser; the shopper can tick it again. The flag is set
   * on the first visit even when the cart has no protection, so a later opt-in is never undone.
   */
  static #firstOptInVisit() {
    try {
      if (localStorage.getItem('st-protection-optin') === '1') return false;
      localStorage.setItem('st-protection-optin', '1');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (oldValue === newValue) return;
    this.#scheduleReconcile();
  }

  /**
   * Waits for every morphed attribute to land before acting on them. A frame is the
   * natural moment, but rAF never fires in a hidden tab, so a timer races it and
   * whichever wins runs the reconcile once.
   */
  #scheduleReconcile() {
    if (this.#reconcileQueued) return;
    this.#reconcileQueued = true;

    const run = () => {
      if (!this.#reconcileQueued) return;
      this.#reconcileQueued = false;
      if (this.isConnected) this.#reconcile();
    };

    requestAnimationFrame(run);
    setTimeout(run, 150);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
  }

  get #sectionId() {
    return this.dataset.sectionId || '';
  }

  get #desired() {
    return this.dataset.desiredVariant || '';
  }

  get #current() {
    const value = this.dataset.currentVariant;
    return value && value !== '0' ? value : '';
  }

  /**
   * Re-prices protection when the cart subtotal has crossed a tier boundary since
   * the line was added.
   */
  /** A server render landed: its data attributes and printed totals are the truth. */
  #syncFromRender() {
    this.#serverVariant = this.#current;
    this.#shown = Boolean(this.#current);
  }

  #reconcile() {
    if (this.#busy) return;
    if (this.#want === null) this.#syncFromRender();

    if (this.#resetPending) {
      this.#resetPending = false;
      if (this.#current) {
        this.#apply({ [this.#current]: 0 }, 'remove');
        return;
      }
    }

    // On by default: a cart without protection gets it added on first render, unless
    // the shopper switched it off earlier in this session.
    if (!this.#current) {
      if (this.#desired && this.dataset.defaultOn === '1' && !this.#declined) {
        this.#apply({ [this.#desired]: 1 }, 'add');
      }
      return;
    }

    // No desired variant means there is nothing left to protect - drop the orphan line.
    if (!this.#desired) {
      this.#apply({ [this.#current]: 0 }, 'remove');
      return;
    }

    if (this.#current === this.#desired) return;

    this.#apply({ [this.#current]: 0, [this.#desired]: 1 }, 'update');
  }

  /**
   * Handles the checkbox change. Bound from Liquid via `on:change="/toggle"`.
   */
  toggle = () => {
    if (!this.refs.toggle) return;
    const want = this.refs.toggle.checked;
    this.#declined = !want;
    this.#paint(want);
    this.#want = want;
    if (!this.#settle) protectionWrite = new Promise((resolve) => (this.#settle = resolve));
    this.#pump();
  };

  /** Sends one write at a time until the cart matches the latest click, then releases Check out. */
  #pump() {
    if (this.#busy) return;
    const has = Boolean(this.#serverVariant);
    if (this.#want === true && !has && this.#desired) {
      this.#apply({ [this.#desired]: 1 }, 'add');
      return;
    }
    if (this.#want === false && has) {
      this.#apply({ [this.#serverVariant]: 0 }, 'remove');
      return;
    }
    this.#want = null;
    if (this.#settle) {
      const settle = this.#settle;
      this.#settle = null;
      protectionWrite = null;
      settle();
    }
  }

  /**
   * Puts the checkbox and every total on screen in the chosen state before the cart write,
   * by adding or taking off the protection price. The server render that follows prints the same.
   * @param {boolean} on
   */
  #paint(on) {
    if (this.refs.toggle) this.refs.toggle.checked = on;
    if (on === this.#shown) return;
    const delta = (on ? 1 : -1) * (Number(this.dataset.price) || 0);
    this.#shown = on;
    const root = this.closest('.st-footer') || this.closest('.cart-totals');
    if (!root || !delta) return;
    const money = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    });
    const targets = root.querySelectorAll(
      '.st-footer__now, .st-footer__was, .st-footer__checkout-total, .cart-totals__total-value'
    );
    for (const el of targets) {
      const text = (el.getAttribute('value') || el.textContent || '').trim();
      const amount = Number(text.replace(/[^0-9.]/g, ''));
      if (!text || !Number.isFinite(amount)) continue;
      const code = text.match(/\s([A-Z]{3})$/);
      const next = money.format((Math.round(amount * 100) + delta) / 100) + (code ? ' ' + code[1] : '');
      if (el.hasAttribute('value')) el.setAttribute('value', next);
      el.textContent = next;
    }
  }

  /** Shows or hides the one-line explanation. Bound from Liquid via `on:click="/info"`. */
  info = (event) => {
    const note = this.refs.note;
    if (!(note instanceof HTMLElement)) return;
    note.hidden = !note.hidden;
    const button = event?.target instanceof Element ? event.target.closest('button') : null;
    button?.setAttribute('aria-expanded', String(!note.hidden));
  };

  /** Remembered for the session so the default-on logic does not re-add what was removed. */
  get #declined() {
    try { return sessionStorage.getItem('st-protection-declined') === '1'; } catch { return false; }
  }
  set #declined(v) {
    try { v ? sessionStorage.setItem('st-protection-declined', '1') : sessionStorage.removeItem('st-protection-declined'); } catch {}
  }

  /**
   * Posts a `/cart/update.js` mutation, asking for this section back, and morphs it.
   *
   * @param {Record<string, number>} updates - Quantities keyed by variant id.
   * @param {'add' | 'remove' | 'update'} intent - What the shopper asked for, for the event payload.
   */
  async #apply(updates, intent) {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    const sectionId = this.#sectionId;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.#busy = true;
    this.setAttribute('loading', '');

    // The section morph does not reliably sync the checkbox's checked property, so the
    // switch is set here to the state the cart is about to be in (and reverted on error).
    if (this.refs.toggle) this.refs.toggle.checked = intent !== 'remove';

    // Keep the hidden-line quantity current before anything reads it: cart-icon.js
    // subtracts this from the ajax cart count, and it fires before the section morphs
    // this attribute to its server-rendered value.
    if (intent === 'add') this.dataset.qty = '1';
    if (intent === 'remove') this.dataset.qty = '0';

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: Object.entries(updates).map(([id, quantity]) => ({ merchandiseId: id, quantity })),
        promise: deferred.promise,
      })
    );
    const seqAtStart = cartMutationSeq;

    try {
      const body = JSON.stringify({
        updates,
        ...(sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {}),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: controller.signal,
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data?.description || data?.message || `Cart update failed (${intent})`);

      const line = Array.isArray(data.items) ? data.items.find((item) => item.handle === 'shipping-protection') : null;
      this.#serverVariant = line ? String(line.variant_id) : '';
      // Another click is queued: skip this repaint, the next write brings the final one.
      const queued = this.#want !== null && this.#want !== Boolean(this.#serverVariant);
      const fresh = !queued && cartMutationSeq === seqAtStart;
      // Fresh section HTML travels with the event so cart-items-component morphs it directly
      // instead of fetching the section again (saves one round trip per toggle / auto-add).
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        ...(fresh && data.sections ? { detail: { sections: data.sections } } : {}),
      });

      const html = sectionId ? data.sections?.[sectionId] : null;
      if (html && fresh) {
        morphSection(sectionId, html, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        deferred.reject(error);
        return;
      }

      deferred.reject(error);

      // Put the checkbox and totals back where the cart actually is.
      this.#want = null;
      this.#paint(Boolean(this.#serverVariant));

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to update shipping protection',
          code: 'INVALID',
        })
      );
    } finally {
      this.#busy = false;
      this.removeAttribute('loading');
      if (!this.isConnected) this.#want = null;
      this.#pump();
    }
  }
}

if (!customElements.get('st-shipping-protection')) {
  customElements.define('st-shipping-protection', ShippingProtection);
}
